// ═══════════════════════════════════════════════════════════
// 솟다 복기 — 행동 패턴 분석 엔진 (sim_trades 읽기전용 집계, 순수함수, TDD)
// design doc: docs/specs/2026-06-06-failmuseum-sim-integration-design.md §3·§4
//
// 게임 체결 엔진(simEngine.ts)과 분리 — 읽기 전용 집계라 충돌 0.
// "실패박물관 판결" = sim_trades 패턴 파생: 회전문·과매매·몰빵·물타기 4종.
// 임계값은 DEFAULT_BEHAVIOR_THRESHOLDS 상수로 분리 → 차후 튜너 UI로 임시 조정.
// ⚠ 행동 판결 단일 출처 = 이 파일 (② 통합 2026-06-10).
//
// 시각 규약: executed_at(체결) 우선, 없으면 ordered_at. pending(price=null)은 집계 제외.
// now는 인자 주입(테스트 결정론 — Date.now 의존 금지).
// ═══════════════════════════════════════════════════════════

const DAY_MS = 86400000;

/** sim_trades 한 행 (집계에 필요한 필드만) */
export interface SimTradeLite {
  id?: string;
  symbol: string;
  side: 'buy' | 'sell';
  qty: number;
  price: number | null;              // 현지통화 체결가 (pending=null)
  realized_pnl_bull?: number | null; // 매도 실현손익 (buy=null)
  amount_bull?: number | null;
  reason_memo?: string | null;       // "왜 샀나" 고백 원천
  reflected_at?: string | null;      // 감정(복기) 완료 시각 — N-15. 고백 유무와 독립적인 "복기됨" 정본
  order_type?: string | null;        // 'delisting' = 상폐 강제 청산 (실패박물관 묘비 카드 — 마이그62)
  museum_hidden?: boolean | null;    // 실패박물관 전시 숨김 (유저 토글 — 마이그62)
  entry_thesis?: unknown | null;     // 매수 명분 jsonb — 존재 여부만 판정에 사용
  ordered_at: string;                // ISO
  executed_at?: string | null;       // ISO (pending이면 null)
  // 진입 스냅샷 (마이그17 entry_*) — 진입 크로스탭 복기용. best-effort라 옵셔널
  entry_rsi?: number | null;
  entry_rsi_pctb?: number | null;      // RSI 볼린저 %B(최근 리듬 상대위치) — 마이그20
  entry_rsi_pct_rank?: number | null;  // RSI 장기 백분위(역사 상대위치) — 호흡2.5
  entry_ma_state?: string | null;
  entry_vol_ratio?: number | null;
  entry_fgi?: number | null;
  entry_regime?: string | null;
  entry_weight_pct?: number | null;
  entry_index_trend?: {
    benchmark?: string | null;
    trend?: string | null;
    [key: string]: unknown;
  } | null;
}

/** sim_positions 한 행 (집계에 필요한 필드만) */
export interface SimPositionLite {
  symbol: string;
  value_bull: number;       // 평가액(BULL)
  avg_price: number;
  pnl_bull?: number | null; // 평가손익(옵션)
  portfolio_total_bull?: number | null; // 현재 비중 판정용 총자산(현금 포함, 옵션)
}

export type BragType = 'general' | 'profit_take' | 'ladder' | 'averaging' | 'hold_long' | 'declared_hold';

export interface BragEligibility {
  type: BragType;
  symbol?: string;
  refTradeId?: string;
  label: string;
}

/** 자랑 카탈로그 v2 판정 상수 — 57_brag_wave3.sql 상단 상수와 동일값 유지. */
export const BRAG_ELIGIBILITY_RULES = {
  holdMinDays: 60,
  averagingMinBuys: 3,
  freshDays: 14,
} as const;

import type { BehaviorKey } from '@core/sim/behavior/types';
export type { BehaviorKey } from '@core/sim/behavior/types';

/** 행동 유형별 임계값 (튜너 조정 대상) */
export interface BehaviorThresholds {
  churnHoldingDays: number;    // 평균 보유기간 < 이 값 → 회전문
  overtradeWindowDays: number; // 과매매 측정 윈도우(일)
  overtradeCount: number;      // 윈도우 내 거래 ≥ 이 값 → 과매매
  concentrationPct: number;    // 단일 종목 평가비중 ≥ 이 값(%) → 몰빵
  // 물타기(v2 · 2026-09-28): 누적 건수 → **최근 추가매수 N건 중 평단 미만 비율**.
  //   누적 건수는 줄 수 없어 개선을 표현하지 못했고, 1회만 해도 영구 판정이었다(설계 2026-09-28-averaging-down-ratio-design.md).
  averagingDownWindow: number;    // 최근 추가매수 몇 건을 보나 (롤링 창)
  averagingDownMinAdds: number;   // 창 안 추가매수 ≥ 이 값 → averaging_down 측정 (표본 가드)
  averagingDownRatioPct: number;  // 평단 미만 추가매수 비율 ≥ 이 값(%) → 물타기
  // 요약 칸 경고(2026-10-03 효신님 결정 "종목별 3회"): 한 번 보유하는 동안 한 종목 물타기 ≥ 이 값 → 경고 표시.
  //   판정 지표(averaging_down)가 아니라 **사실 수치 옆 경고**다. 앱 처방 "한 종목당 물타기는 세 번까지만"(fmPersonas)과 같은 숫자.
  averagingDownPerPositionWarn: number;
  // Step A #23 — 진입 타이밍 기반(표본 가드 + 비율 임계). ⚠ 백테스트 분별력 검증 전엔 약한 진단(단정 라벨 금지).
  fomoChaseMinBuys: number;      // 진단가능 매수 ≥ 이 값 → fomo_chase 측정 (표본 가드)
  fomoChaseRatioPct: number;     // weak_chase(약한 종목 상단 추격) 비율 ≥ 이 값(%) → FOMO 추격
  fallingKnifeMinLosses: number; // 손실 roundtrip ≥ 이 값 → falling_knife 측정 (표본 가드)
  fallingKnifeRatioPct: number;  // weak_falling(약한 종목 낙하 매수) 비율 ≥ 이 값(%) → 떨어지는 칼날
  noFocusSymbols: number;         // 순보유 종목 ≥ 이 값 → 백화점 분산
  breakevenMinSells: number;      // 실현매도 ≥ 이 값 → breakeven_exit 측정 (표본 가드)
  breakevenRatioPct: number;      // 본전 근처 매도 비율 ≥ 이 값(%) → 본전 탈출
  breakevenBandPct: number;       // |실현손익|/매도금액 < 이 값(%) → 본전 근처
  noThesisMinBuys: number;        // 체결 매수 ≥ 이 값 → no_thesis 측정 (표본 가드)
  noThesisRatioPct: number;       // 매수 명분 무기록 비율 ≥ 이 값(%) → 묻지마 승차
}

/** 서버 복기 호환 행동 임계값 — 포지션 평가액이 필요한 concentration은 제외. */
export const BEHAVIOR_THRESHOLDS = {
  churnHoldingDays: 3,
  overtradeWindowDays: 7,
  overtradeCount: 10,
  averagingDownWindow: 20,
  averagingDownMinAdds: 5,
  averagingDownRatioPct: 50,
  averagingDownPerPositionWarn: 3,
  fomoChaseMinBuys: 5,
  fomoChaseRatioPct: 40,
  fallingKnifeMinLosses: 3,
  fallingKnifeRatioPct: 40,
  noFocusSymbols: 8,
  breakevenMinSells: 5,
  breakevenRatioPct: 40,
  breakevenBandPct: 1,
  noThesisMinBuys: 5,
  noThesisRatioPct: 80,
} as const;

// design doc §11-2 MVP 가정 (eyeGrade와 동일 패턴 — 운영 데이터로 보정)
export const DEFAULT_BEHAVIOR_THRESHOLDS: BehaviorThresholds = {
  ...BEHAVIOR_THRESHOLDS,
  concentrationPct: 50,
};

const LABELS: Record<BehaviorKey, string> = {
  churn: '회전문 트레이더',
  overtrade: '과매매',
  concentration: '몰빵 투자',
  averaging_down: '물타기·손절 지연',
  fomo_chase: 'FOMO 추격',
  falling_knife: '떨어지는 칼날',
  no_focus: '백화점 분산',
  breakeven_exit: '본전 탈출',
  no_thesis: '묻지마 승차',
};

// 손실 굿즈 환산 단위 (design doc §3 "금액=IP굿즈환산" — 원화 표기 없이 추상화)
// 황금알=큰 단위, 후라이드=작은 단위. 통화 표기 금지(실패박물관 톤).
export const GOODS_UNIT = { eggBull: 1_000_000, chickenBull: 20_000 };

/**
 * 손실 BULL → 굿즈 환산. 큰 손실은 황금알, 나머지는 후라이드.
 * 자동 손익(realized_pnl_bull) 기반 — 슬라이더 대신 실제 손실을 굿즈로.
 */
export function lossToGoods(lossBull: number): { eggs: number; chickens: number } {
  const abs = Math.max(0, Math.abs(lossBull));
  const eggs = Math.floor(abs / GOODS_UNIT.eggBull);
  const chickens = Math.floor((abs - eggs * GOODS_UNIT.eggBull) / GOODS_UNIT.chickenBull);
  return { eggs, chickens };
}

/** 체결 시각(ms). executed_at 우선, 없으면 ordered_at */
const tsOf = (t: SimTradeLite): number =>
  new Date(t.executed_at || t.ordered_at).getTime();

/** 체결된(price 있는) 거래만 시간순 정렬 */
const filledSorted = (trades: SimTradeLite[]): SimTradeLite[] =>
  trades.filter((t) => t.price != null).sort((a, b) => tsOf(a) - tsOf(b));

/**
 * 판정 상한 고정(look-ahead cut) — now 이후 체결 거래 배제.
 * 과거 now를 주입하는 모든 경로(복기 재생·백테스트·튜너)는 이 함수로 **선필터 의무** —
 * avgHoldingDays·averagingDownCount·entryRoundTrips·openLots는 now를 받지 않고 전체 이력을
 * 순회하므로, 입력에 미래 거래가 섞이면 과거 시점 lot이 미래 매도에 소진돼 판정이 오염된다
 * (웨이브2 design §3.2 벡터 B — [[feedback_timeseries_lookahead_leak]]).
 * 판정 함수 **내부** 무조건 t≤now 컷은 기각(§3.4): 라이브 경로에서 기기 시계가 서버보다
 * 느리면(skew) 방금 체결이 판정에서 빠지는 사용자 가시 버그 — 라이브는 선필터 없이 전체 이력 사용.
 * 회귀 계약: reviewMode.test.ts "look-ahead 회귀" 블록 (멱등 + 선필터 미래무관 + 무필터 뚫림 특성화).
 */
export function tradesAsOf(trades: SimTradeLite[], now: number): SimTradeLite[] {
  return trades.filter((t) => tsOf(t) <= now);
}

/**
 * 회전문 지표 — 완결된 매수→매도 라운드트립의 수량가중 평균 보유일.
 * FIFO 매칭(오래된 lot부터 소진). 완결 매도가 없으면 null.
 */
export function avgHoldingDays(trades: SimTradeLite[]): number | null {
  const lots = new Map<string, { ts: number; qty: number }[]>(); // 종목별 매수 lot 큐
  let weightedDays = 0;
  let matchedQty = 0;

  for (const t of filledSorted(trades)) {
    const q = lots.get(t.symbol) || [];
    if (t.side === 'buy') {
      q.push({ ts: tsOf(t), qty: t.qty });
    } else {
      let remain = t.qty;
      const sellTs = tsOf(t);
      while (remain > 0 && q.length > 0) {
        const lot = q[0];
        const take = Math.min(remain, lot.qty);
        weightedDays += ((sellTs - lot.ts) / DAY_MS) * take;
        matchedQty += take;
        lot.qty -= take;
        remain -= take;
        if (lot.qty <= 1e-9) q.shift();
      }
    }
    lots.set(t.symbol, q);
  }
  return matchedQty === 0 ? null : weightedDays / matchedQty;
}

/** 과매매 지표 — now 기준 windowDays 이내 체결 거래 수 */
export function recentTradeCount(trades: SimTradeLite[], now: number, windowDays: number): number {
  const cutoff = now - windowDays * DAY_MS;
  return trades.filter((t) => t.price != null && tsOf(t) >= cutoff).length;
}

/** 몰빵 지표 — 보유 포지션 최대 평가비중(%, 0~100). 보유 없으면 0 */
export function maxConcentrationPct(positions: SimPositionLite[]): number {
  const total = positions.reduce((s, p) => s + (p.value_bull || 0), 0);
  if (total <= 0) return 0;
  const max = Math.max(...positions.map((p) => p.value_bull || 0));
  return (max / total) * 100;
}

/**
 * 물타기 지표 — 평가손실 상태(직전 평단보다 낮은 가격)에서 추가매수한 횟수.
 * 신규 매수(기존 보유 0)는 제외. 종목별 평단을 시간순으로 누적 추적.
 */
export function averagingDownCount(trades: SimTradeLite[]): number {
  const pos = new Map<string, { qty: number; avg: number }>();
  let count = 0;

  for (const t of filledSorted(trades)) {
    const cur = pos.get(t.symbol) || { qty: 0, avg: 0 };
    const price = t.price as number;
    if (t.side === 'buy') {
      if (cur.qty > 0 && price < cur.avg) count++; // 보유 중 + 평단 미만 = 물타기
      const qty = cur.qty + t.qty;
      const avg = qty === 0 ? 0 : (cur.qty * cur.avg + t.qty * price) / qty;
      pos.set(t.symbol, { qty, avg });
    } else {
      const qty = Math.max(0, cur.qty - t.qty);
      pos.set(t.symbol, { qty, avg: qty === 0 ? 0 : cur.avg });
    }
  }
  return count;
}

/**
 * 추가매수 내역(2026-10-03) — 보유 중 같은 종목을 더 산 매수를 **평단 기준**으로 가른다.
 * - 물타기 = 평단보다 낮게 산 추가매수(평단을 낮춘다) · 불타기 = 평단보다 높게 산 추가매수
 *   · 평단과 같은 가격은 어느 쪽도 아니다 · 보유 0에서 산 신규 매수도 어느 쪽도 아니다.
 * - maxAveragingDownPerPosition = **한 번 보유하는 동안**(전량 매도 전까지) 한 종목에서 물타기한 최대 횟수.
 *   전량 매도 후 다시 사면 새 보유로 0부터 센다 — "한 종목 샀을 때 기준, 들고 있을 때 또 사면"(효신님 정의)의 단위.
 *   일부만 팔면 같은 보유가 이어진다.
 * - 불타기는 판정·경고하지 않는다: 오르는 종목에 싣는 건 추세 추종의 정석일 수도, 고점 추격일 수도 있어
 *   좋고 나쁨의 방향이 없다. 사실 수치로만 보여주고 해석은 AI 복기에 맡긴다.
 * - averagingDown 합계는 averagingDownCount와 같은 규칙이다(테스트로 고정).
 */
export function addOnBreakdown(trades: SimTradeLite[]): {
  averagingDown: number; pyramiding: number; maxAveragingDownPerPosition: number;
} {
  const pos = new Map<string, { qty: number; avg: number; downs: number }>();
  let averagingDown = 0;
  let pyramiding = 0;
  let maxPerPosition = 0;
  for (const t of filledSorted(trades)) {
    const cur = pos.get(t.symbol) || { qty: 0, avg: 0, downs: 0 };
    const price = t.price as number;
    if (t.side === 'buy') {
      let downs = cur.qty > 0 ? cur.downs : 0; // 보유 0에서 산 매수 = 새 보유 시작
      if (cur.qty > 0 && price < cur.avg) {
        averagingDown++;
        downs++;
        if (downs > maxPerPosition) maxPerPosition = downs;
      } else if (cur.qty > 0 && price > cur.avg) {
        pyramiding++;
      }
      const qty = cur.qty + t.qty;
      const avg = qty === 0 ? 0 : (cur.qty * cur.avg + t.qty * price) / qty;
      pos.set(t.symbol, { qty, avg, downs });
    } else {
      const qty = Math.max(0, cur.qty - t.qty);
      // 전량 매도 = 보유 끝 → 물타기 횟수도 끝. 일부 매도는 같은 보유가 이어진다.
      pos.set(t.symbol, { qty, avg: qty === 0 ? 0 : cur.avg, downs: qty === 0 ? 0 : cur.downs });
    }
  }
  return { averagingDown, pyramiding, maxAveragingDownPerPosition: maxPerPosition };
}

/**
 * 물타기 비율(v2) — **최근 추가매수 window건** 중 평단 미만에서 산 비율(%).
 * - 추가매수 = 이미 보유 중인 종목을 더 산 매수(신규 매수 제외 — averagingDownCount와 같은 규칙).
 * - 창을 "기간"이 아니라 "건수"로 잡는 이유: 기간 창은 매매를 쉬면 표본이 사라지고,
 *   전체 누적은 오래 쓸수록 과거가 지배해 습관이 바뀌어도 움직이지 않는다.
 * - have < minAdds면 pct=null(측정 불가) — 0%로 내리면 "물타기 안 함"으로 읽힌다.
 */
export function averagingDownRate(
  trades: SimTradeLite[], window: number, minAdds: number,
): { pct: number | null; have: number } {
  const recent = averagingDownWindow(trades, window).adds;
  const have = recent.length;
  if (have < minAdds) return { pct: null, have };
  return { pct: Math.round((recent.filter((a) => a.down).length / have) * 100), have };
}

/** 지표와 손익이 같은 추가매수 창을 공유한다. 보유 구간은 전량 매도까지 이어진다. */
function averagingDownWindow(trades: SimTradeLite[], window: number) {
  type Position = { qty: number; avg: number; pnl: number; closed: boolean };
  const positions = new Map<string, Position>();
  const adds: { down: boolean; position: Position }[] = [];
  for (const t of filledSorted(trades)) {
    let cur = positions.get(t.symbol);
    if (t.side === 'buy') {
      if (!cur || cur.closed) {
        cur = { qty: 0, avg: 0, pnl: 0, closed: false };
        positions.set(t.symbol, cur);
      }
      if (cur.qty > 0) adds.push({ down: (t.price as number) < cur.avg, position: cur });
      const qty = cur.qty + t.qty;
      cur.avg = qty === 0 ? 0 : (cur.qty * cur.avg + t.qty * (t.price as number)) / qty;
      cur.qty = qty;
    } else if (cur && !cur.closed) {
      cur.pnl += Number(t.realized_pnl_bull) || 0;
      cur.qty = Math.max(0, cur.qty - t.qty);
      cur.closed = cur.qty === 0;
      if (cur.closed) cur.avg = 0;
    }
  }
  return { adds: adds.slice(-Math.max(1, window)) };
}

const isChase = (t: Parameters<typeof diagnoseEntryTiming>[0]) =>
  ['weak_chase', 'weak_chase_hi_vol'].includes(diagnoseEntryTiming(t) ?? '');
const isFallingLoss = (r: EntryRoundTrip) => r.pnl < 0 &&
  ['weak_falling', 'weak_falling_hi_vol'].includes(diagnoseEntryTiming(r) ?? '');
const hasNoThesis = (t: SimTradeLite) =>
  (t.reason_memo == null || t.reason_memo.trim() === '') && isEmptyEntryThesis(t.entry_thesis);

export type BehaviorPnl = { sum: number | null; n: number };

/** 청산 완료한 분자 거래만 귀속한다. 물타기는 같은 보유 구간을 한 번만 센다. */
export function behaviorPnl(
  trades: SimTradeLite[], thresholds: BehaviorThresholds = DEFAULT_BEHAVIOR_THRESHOLDS,
): Partial<Record<BehaviorKey, BehaviorPnl>> {
  const sum = (values: number[]): BehaviorPnl => ({
    sum: values.length >= 3 ? values.reduce((a, b) => a + b, 0) : null, n: values.length,
  });
  const entries = entryRoundTripsWithEntry(trades);
  const downPositions = new Set(averagingDownWindow(trades, thresholds.averagingDownWindow)
    .adds.filter((a) => a.down && a.position.closed).map((a) => a.position));
  return {
    averaging_down: sum([...downPositions].map((p) => p.pnl)),
    fomo_chase: sum(entries.filter(({ entry }) => isChase(entry)).map(({ roundTrip }) => roundTrip.pnl)),
    falling_knife: sum(entries.filter(({ roundTrip }) => isFallingLoss(roundTrip)).map(({ roundTrip }) => roundTrip.pnl)),
    no_thesis: sum(entries.filter(({ entry }) => hasNoThesis(entry)).map(({ roundTrip }) => roundTrip.pnl)),
  };
}

/** 손실 카드 후보 — 매도 실현손실(<0) 거래를 최근순으로 추출 (고백 원천)
 *  ⚠ 복기 여부는 보지 않는다. 실패박물관 수장고·복기 화면의 카드 선택처럼
 *     "이미 복기한 카드도 보여야 하는" 곳이 이 함수를 쓴다. */
export function extractLossCards(trades: SimTradeLite[]): SimTradeLite[] {
  return trades
    .filter((t) => t.side === 'sell' && (t.realized_pnl_bull ?? 0) < 0)
    .sort((a, b) => tsOf(b) - tsOf(a));
}

/** 아직 복기하지 않은 손실 카드 — "복기할 손실 N건" 같은 **할 일 카운터** 전용.
 *  2026-09-08 SE3 순회 발견: 홈과 복기 허브가 extractLossCards().length를 그대로 써서
 *  한 건을 복기한 뒤에도 카운트가 줄지 않았다(효신님 "한 개 작성했는데 2건").
 *  복기 완료 정본은 `reflected_at`(첫 시점 고정·멱등)이다. */
export function extractUnreflectedLossCards(trades: SimTradeLite[]): SimTradeLite[] {
  return extractLossCards(trades).filter((t) => !t.reflected_at);
}

/** 임계 초과한 행동 유형 플래그 */
export interface BehaviorFlag {
  key: BehaviorKey;
  label: string;     // 판결 라벨
  value: number;     // 측정값
  threshold: number; // 임계값
  severity: number;  // 초과 심각도 (정규화 — primary 선정용, 1 초과 시 위반)
}

export interface BehaviorVerdict {
  flags: BehaviorFlag[];        // 임계 초과 유형들
  primary: BehaviorFlag | null; // 가장 두드러진 유형 (없으면 null = 건전)
}

/** 서버 AI 복기 입력용 플래그 — 몰빵·severity·primary 없이 누적 행동 수치만 전달. */
export type BehaviorStatsFlag = Omit<BehaviorFlag, 'severity'>;

/** entry_thesis jsonb의 실질 내용 존재 여부 — nullish·빈 객체·빈 배열·공백 문자열은 빈값. */
const isEmptyEntryThesis = (value: unknown): boolean => {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  if (typeof value === 'string') return value.trim() === '';
  return false;
};

/**
 * 종합 판결 — 4종 지표를 측정해 임계 초과분을 flags로, severity 최대를 primary로.
 * thresholds 주입 가능(튜너). primary.key → voice.js verdict/prescribe 맥락 선택.
 * ⚠ 과거 now 주입 경로(복기 재생·백테스트·튜너)는 tradesAsOf(trades, now) 선필터 의무 (look-ahead cut).
 */
/**
 * 지표 1종의 **측정 결과** — flags와 달리 임계 미달·표본 부족도 항상 담긴다.
 *
 * 🔴 2026-08-03 신설 이유: classifyBehavior는 `if (초과) flags.push(...)` 구조라
 *   유저가 개선되어 임계 아래로 내려가면 그 지표가 **배열에서 사라진다**. 그러면 회차별 스냅샷을
 *   이어붙여도 빈칸이 (a) 개선인지 (b) 표본이 모자라 측정을 못 한 건지 구분할 수 없어
 *   **행동 개선 추이를 그릴 수 없다**(복기 효과를 유저에게 보여주려면 이게 전제다).
 *   → 측정(measure)과 판정(classify)을 분리한다. 판정은 측정 결과를 거르기만 하므로 갈라지지 않는다.
 */
export interface BehaviorMetric {
  key: BehaviorKey;
  label: string;
  value: number | null;           // null = 표본 미달 등으로 측정 불가 (0과 반드시 구분된다)
  threshold: number;
  exceeded: boolean;
  severity: number;               // exceeded일 때만 의미 있음
  direction: 'lower' | 'higher';  // 'higher' = 값이 클수록 건전 (회전문의 평균 보유일)
  sample?: { have: number; need: number }; // 표본 가드가 있는 지표의 현재/필요 표본
}

/** 서버 AI 복기 입력용 — severity(UI 전용)만 뺀 측정값. */
export type BehaviorStatsMetric = Omit<BehaviorMetric, 'severity'> & { pnl?: BehaviorPnl };

/**
 * 행동 지표 **전량 측정** — 9종을 항상 반환한다(초과 여부와 무관).
 * ⚠ 순서는 구 classifyBehavior의 push 순서와 동일하게 유지한다 — flags 순서에 의존하는 소비자가 있다.
 */
export function measureBehavior(
  input: {
    trades: SimTradeLite[]; positions: SimPositionLite[]; now: number;
    /**
     * 몰빵 비중(%)을 **직접 주입**한다. 주면 positions보다 우선한다.
     *
     * 🔴 2026-08-03 신설 — 서버 복기 경로(behaviorMetrics)는 positions를 []로 넘겨서
     *   concentration이 9종 중 **혼자만 영구 null**이었다. 개선 추이 화면에서 한 칸이 계속 빈다.
     *   그런데 sim_positions에는 `value_bull`(평가액) 컬럼이 아예 없다 — 그 값은 앱이 시세를 붙여
     *   만들어 넘기는 것이라 서버는 maxConcentrationPct를 쓸 수 없다.
     *   → 서버는 buildPortfolio가 이미 **BULL로 환산해 둔 매수원가 기준 비중**을 넘긴다.
     *   ⚠ 앱(ReflectScreen)의 평가액 기준과 **분모가 다르다**. 그래도 이쪽이 복기에 더 맞다:
     *     평가액 비중은 주가가 오르기만 해도 몰빵으로 잡혀 **유저 행동이 아닌 것이 섞인다**
     *     (설계 §5 원칙 — 주 지표는 시장이 아니라 유저가 통제하는 행동).
     */
    concentrationPct?: number | null;
  },
  thresholds: BehaviorThresholds = DEFAULT_BEHAVIOR_THRESHOLDS,
): BehaviorMetric[] {
  const { trades, positions, now } = input;
  const out: BehaviorMetric[] = [];
  const push = (
    key: BehaviorKey, value: number | null, threshold: number,
    exceeded: boolean, severity: number, direction: 'lower' | 'higher',
    sample?: { have: number; need: number },
  ) => { out.push({ key, label: LABELS[key], value, threshold, exceeded, severity, direction, ...(sample ? { sample } : {}) }); };

  // 회전문: 보유일이 **짧을수록** 심함 → severity = 임계/측정값
  const hold = avgHoldingDays(trades);
  push('churn', hold, thresholds.churnHoldingDays,
    hold != null && hold < thresholds.churnHoldingDays,
    hold != null && hold > 0 ? thresholds.churnHoldingDays / hold : thresholds.churnHoldingDays, 'higher');

  // 과매매: 많을수록 심함
  const cnt = recentTradeCount(trades, now, thresholds.overtradeWindowDays);
  push('overtrade', cnt, thresholds.overtradeCount, cnt >= thresholds.overtradeCount,
    cnt / thresholds.overtradeCount, 'lower');

  // 몰빵: 주입값(서버·매수원가 기준) 우선, 없으면 positions 평가액 기준(앱).
  //   ⚠ 둘 다 없으면 "0%"가 아니라 **측정 불가(null)**여야 한다 — 0으로 찍으면 개선한 것처럼 보인다.
  const injected = input.concentrationPct;
  const conc = Number.isFinite(injected as number) ? (injected as number)
    : positions && positions.length > 0 ? maxConcentrationPct(positions)
    : null;
  push('concentration', conc, thresholds.concentrationPct,
    conc != null && conc >= thresholds.concentrationPct,
    conc != null ? conc / thresholds.concentrationPct : 0, 'lower');

  // 물타기(v2): 최근 추가매수 중 평단 미만 비율. 표본 가드 미달이면 value=null.
  //   ⚠ 정의가 v1(누적 건수)과 다르다 — 회차 간 비교는 behaviorTrendCore.comparableMetrics를 거칠 것.
  const avgDown = averagingDownRate(trades, thresholds.averagingDownWindow, thresholds.averagingDownMinAdds);
  push('averaging_down', avgDown.pct, thresholds.averagingDownRatioPct,
    avgDown.pct != null && avgDown.pct >= thresholds.averagingDownRatioPct,
    avgDown.pct != null ? avgDown.pct / thresholds.averagingDownRatioPct : 0, 'lower',
    { have: avgDown.have, need: thresholds.averagingDownMinAdds });

  // FOMO 추격(#23): 매수 중 weak_chase 비율. 표본 가드 미달이면 value=null.
  const diagBuys = trades
    .filter((t) => t.side === 'buy')
    .filter((t) => diagnoseEntryTiming(t) != null);
  const fomoOk = diagBuys.length >= thresholds.fomoChaseMinBuys;
  const fomoPct = fomoOk
    ? Math.round((diagBuys.filter(isChase).length / diagBuys.length) * 100)
    : null;
  push('fomo_chase', fomoPct, thresholds.fomoChaseRatioPct,
    fomoPct != null && fomoPct >= thresholds.fomoChaseRatioPct,
    fomoPct != null ? fomoPct / thresholds.fomoChaseRatioPct : 0, 'lower',
    { have: diagBuys.length, need: thresholds.fomoChaseMinBuys });

  // 떨어지는 칼날(#23): 손실 청산 중 weak_falling 비율.
  // 🔴 2026-09-27: 진입 스냅샷(entry_*)이 없어 **진단할 수 없는** 손실까지 분모에 넣어 "칼날 아님"으로 셌다
  //   → 스냅샷이 빠질수록 비율이 낮아지고, 전부 빠지면 측정 불가가 0%(건전)로 보였다(13F 벤치에서 적발).
  //   fomo_chase와 같은 규칙으로 **진단 가능한 손실만** 분모·표본에 넣는다(측정 불가 ≠ 0).
  const lossDiags = entryRoundTrips(trades)
    .filter((r) => r.pnl < 0)
    .filter((r) => diagnoseEntryTiming(r) != null);
  const fkOk = lossDiags.length >= thresholds.fallingKnifeMinLosses;
  const fkPct = fkOk
    ? Math.round((lossDiags.filter(isFallingLoss).length / lossDiags.length) * 100)
    : null;
  push('falling_knife', fkPct, thresholds.fallingKnifeRatioPct,
    fkPct != null && fkPct >= thresholds.fallingKnifeRatioPct,
    fkPct != null ? fkPct / thresholds.fallingKnifeRatioPct : 0, 'lower',
    { have: lossDiags.length, need: thresholds.fallingKnifeMinLosses });

  // 백화점 분산: openLots 순보유 종목 수. 표본 가드 없음(항상 측정 가능).
  const openSymbols = new Set(openLots(trades).map((lot) => lot.symbol)).size;
  push('no_focus', openSymbols, thresholds.noFocusSymbols, openSymbols >= thresholds.noFocusSymbols,
    openSymbols / thresholds.noFocusSymbols, 'lower');

  // 본전 탈출: 실현매도 중 본전 근처 비율.
  const realizedSells = trades.filter((t) =>
    t.side === 'sell' && t.realized_pnl_bull != null && (t.amount_bull ?? 0) > 0);
  const beOk = realizedSells.length >= thresholds.breakevenMinSells;
  const bePct = beOk
    ? Math.round((realizedSells.filter((t) =>
        (Math.abs(t.realized_pnl_bull as number) / (t.amount_bull as number)) * 100 < thresholds.breakevenBandPct
      ).length / realizedSells.length) * 100)
    : null;
  push('breakeven_exit', bePct, thresholds.breakevenRatioPct,
    bePct != null && bePct >= thresholds.breakevenRatioPct,
    bePct != null ? bePct / thresholds.breakevenRatioPct : 0, 'lower',
    { have: realizedSells.length, need: thresholds.breakevenMinSells });

  // 묻지마 승차: 체결 매수 중 명분 무기록 비율.
  const filledBuys = trades.filter((t) => t.side === 'buy' && t.price != null);
  const ntOk = filledBuys.length >= thresholds.noThesisMinBuys;
  const ntPct = ntOk
    ? Math.round((filledBuys.filter(hasNoThesis).length / filledBuys.length) * 100)
    : null;
  push('no_thesis', ntPct, thresholds.noThesisRatioPct,
    ntPct != null && ntPct >= thresholds.noThesisRatioPct,
    ntPct != null ? ntPct / thresholds.noThesisRatioPct : 0, 'lower',
    { have: filledBuys.length, need: thresholds.noThesisMinBuys });

  return out;
}

/**
 * 종합 판결 — 측정값 중 **임계 초과분만** flags로, severity 최대를 primary로.
 * thresholds 주입 가능(튜너). primary.key → voice.js verdict/prescribe 맥락 선택.
 * ⚠ 과거 now 주입 경로(복기 재생·백테스트·튜너)는 tradesAsOf(trades, now) 선필터 의무 (look-ahead cut).
 */
export function classifyBehavior(
  input: { trades: SimTradeLite[]; positions: SimPositionLite[]; now: number },
  thresholds: BehaviorThresholds = DEFAULT_BEHAVIOR_THRESHOLDS,
): BehaviorVerdict {
  const flags: BehaviorFlag[] = measureBehavior(input, thresholds)
    .filter((m) => m.exceeded && m.value != null)
    .map((m) => ({ key: m.key, label: m.label, value: m.value as number, threshold: m.threshold, severity: m.severity }));

  const primary = flags.length === 0
    ? null
    : flags.reduce((a, b) => (b.severity > a.severity ? b : a));
  return { flags, primary };
}

/**
 * 서버 AI 복기 입력 호환 판정.
 * classifyBehavior의 거래 기반 8종을 재사용하되 포지션 전용 concentration과 UI용 severity/primary는 제외한다.
 */
export function behaviorFlags(trades: SimTradeLite[], now: number): BehaviorStatsFlag[] {
  return classifyBehavior({ trades, positions: [], now }).flags.map((flag) => ({
    key: flag.key,
    label: flag.label,
    value: flag.key === 'churn' ? Math.round(flag.value * 10) / 10 : flag.value,
    threshold: flag.threshold,
  }));
}

/**
 * 서버 AI 복기 입력 — **전량 측정값**(임계 미달·측정 불가 포함).
 * behaviorFlags(초과분만)와 병행한다: 프롬프트는 flags를 쓰고, **개선 추이 시각화는 이쪽을 쓴다**.
 * ⚠ value=null은 "0"이 아니라 "측정 못 함"이다 — 차트에서 0으로 그리면 개선한 것처럼 보인다.
 */
export function behaviorMetrics(
  trades: SimTradeLite[], now: number,
  /** 몰빵 비중(%) — buildPortfolio의 최대 weightPct(BULL 환산 매수원가 기준). 없으면 concentration은 측정 불가로 남는다. */
  concentrationPct: number | null = null,
): BehaviorStatsMetric[] {
  const pnl = behaviorPnl(trades);
  return measureBehavior({ trades, positions: [], now, concentrationPct }).map((m) => ({
    key: m.key,
    label: m.label,
    value: m.key === 'churn' && m.value != null ? Math.round(m.value * 10) / 10 : m.value,
    threshold: m.threshold,
    exceeded: m.exceeded,
    direction: m.direction,
    ...(m.sample ? { sample: m.sample } : {}),
    ...(pnl[m.key] ? { pnl: pnl[m.key] } : {}),
  }));
}

// ═══════════════════════════════════════════════════════════
// 진입 크로스탭 — entry_* 구간별 승률·평균손익 (로드맵 1 무료 룰 복기, LLM 0)
// 진입 크로스탭의 단일 정본도 이 파일이다.
// entry_*는 매수 행, realized_pnl_bull은 매도 행 → FIFO로 매수 lot에 청산 손익을
// 수량비례 귀속해야 "진입 시점 지표 → 결과"가 정확. (매도 행 entry_*는 청산 시점값이라 부적합)
// ═══════════════════════════════════════════════════════════

export type RsiBand = 'oversold' | 'neutral' | 'overbought';
export type WeightBand = 'small' | 'normal' | 'heavy';
export type RelPos = 'high' | 'mid' | 'low';
export type EntryTiming =
  | 'strong_push' | 'weak_chase' | 'strong_dip' | 'weak_falling' | 'neutral'
  | 'strong_push_hi_vol' | 'weak_chase_hi_vol' | 'strong_dip_hi_vol' | 'weak_falling_hi_vol';

/** 완결 진입 라운드트립 — entry_* 스냅샷 + 귀속된 실현손익 */
export interface EntryRoundTrip {
  symbol: string;
  entry_rsi: number | null;
  entry_rsi_pctb: number | null;
  entry_rsi_pct_rank: number | null;
  entry_ma_state: string | null;
  entry_vol_ratio: number | null;
  entry_fgi: number | null;
  entry_regime: string | null;
  entry_weight_pct: number | null;
  entry_index_trend: SimTradeLite['entry_index_trend'];
  pnl: number; // 귀속된 실현손익(BULL)
  qty: number; // 진입 수량
}

/** 크로스탭 한 구간 */
export interface EntryCrosstabRow {
  key: string;
  count: number;
  winRate: number; // %
  avgPnl: number;  // 평균 실현손익(BULL)
  totalPnl: number;
}

/** 진입 크로스탭 묶음 */
export interface EntryStats {
  roundTripCount: number;
  byEntryRsi: EntryCrosstabRow[];
  byEntryTiming: EntryCrosstabRow[];
  byEntryRegime: EntryCrosstabRow[];
  byTimingRegime: EntryCrosstabRow[];
  byEntryWeight: EntryCrosstabRow[];
  byEntryIndexTrend: EntryCrosstabRow[];
}

/** RSI 버킷 — 진입 RSI를 과매도(<30)·중립(30~70)·과매수(≥70)로. 표준 30/70 경계 */
export function bucketRsi(rsi: number | null | undefined): RsiBand | null {
  if (rsi == null) return null;
  const v = Number(rsi);
  if (!Number.isFinite(v)) return null;
  if (v < 30) return 'oversold';
  if (v < 70) return 'neutral';
  return 'overbought';
}

/**
 * 진입 상대위치 밴드 — 고정 30/70 절대임계의 함정 보정(fmkorea 9959890244·9959902866).
 * 폴백 체인: ① RSI 볼린저 %B(최근 20봉 리듬) → ② 장기 백분위(상장이후 역사, 호흡2.5)
 *            → ③ 절대 RSI(과거 데이터 폴백). 첫 유효값 사용.
 * 상대위치 폴백 체인의 단일 정본.
 */
export function relPosBand(
  rsiPctB: number | null | undefined,
  rsiPctRank: number | null | undefined,
  rsiAbs: number | null | undefined,
): RelPos | null {
  const norm = (x: number | null | undefined) =>
    x != null && Number.isFinite(Number(x)) ? Number(x) : null;
  const pb = norm(rsiPctB);
  if (pb != null) return pb > 0.8 ? 'high' : pb < 0.2 ? 'low' : 'mid';
  const pr = norm(rsiPctRank);
  if (pr != null) return pr > 0.8 ? 'high' : pr < 0.2 ? 'low' : 'mid';
  const ab = norm(rsiAbs);
  if (ab != null) return ab >= 70 ? 'high' : ab < 30 ? 'low' : 'mid';
  return null;
}

/**
 * 결합 진단 — 상대위치 × 추세(ma_state) × 거래량(vol_ratio). fmkorea 2편 결론 코드화:
 * "강한 종목 과매수(strong_push)는 정상, 약한 종목 과매수(weak_chase)는 위험" 구분.
 * 진입 타이밍 로직·라벨·경계의 단일 정본.
 */
export function diagnoseEntryTiming(t: {
  entry_rsi?: number | null;
  entry_rsi_pctb?: number | null;
  entry_rsi_pct_rank?: number | null;
  entry_ma_state?: string | null;
  entry_vol_ratio?: number | null;
}): EntryTiming | null {
  const band = relPosBand(t.entry_rsi_pctb, t.entry_rsi_pct_rank, t.entry_rsi);
  if (band == null) return null;
  const up = t.entry_ma_state === 'up';
  let label: string;
  if (band === 'high') label = up ? 'strong_push' : 'weak_chase';
  else if (band === 'low') label = up ? 'strong_dip' : 'weak_falling';
  else label = 'neutral';
  const vr = Number(t.entry_vol_ratio);
  if (label !== 'neutral' && Number.isFinite(vr) && vr >= 2) label += '_hi_vol';
  return label as EntryTiming;
}

/**
 * 진입 타이밍 × 시장 레짐 결합 키 — fmkorea 3편(옵션플로우/조정장) 코드화:
 * "같은 매매도 시장 국면에 따라 의미가 다르다 — 조정·하락장에선 개별주 강도가 안 통하고 지수 흐름이 지배".
 * 종목 진단 × 시장 레짐(entry_regime)을 교차해 시장 정렬/역행을 데이터로 축적(하드코딩 라벨 ❌ = 고정임계 함정 회피).
 * 반환 `${timing}@${regime}` | null.
 */
export function timingRegimeKey(t: {
  entry_rsi?: number | null;
  entry_rsi_pctb?: number | null;
  entry_rsi_pct_rank?: number | null;
  entry_ma_state?: string | null;
  entry_vol_ratio?: number | null;
  entry_regime?: string | null;
}): string | null {
  const timing = diagnoseEntryTiming(t);
  const regime = t.entry_regime;
  if (timing == null || regime == null || regime === '') return null;
  return `${timing}@${regime}`;
}

/** 비중 버킷 — 진입 포트폴리오 비중%를 소액(<10)·보통(10~30)·집중(≥30)으로 (몰빵 리스크 관점) */
export function bucketWeight(pct: number | null | undefined): WeightBand | null {
  if (pct == null) return null;
  const v = Number(pct);
  if (!Number.isFinite(v)) return null;
  if (v < 10) return 'small';
  if (v < 30) return 'normal';
  return 'heavy';
}

/**
 * FIFO 진입 라운드트립 — 매수 lot에 entry_* 스냅샷을 싣고, 매도 시 realized_pnl_bull을
 * 소비 수량 비례로 lot에 누적. lot이 완전 청산되면 완결로 확정(부분 청산은 제외).
 * avgHoldingDays와 동일한 FIFO 골격 — 보유일 대신 손익을 lot에 귀속.
 */
export function entryRoundTrips(trades: SimTradeLite[]): EntryRoundTrip[] {
  return entryRoundTripsWithEntry(trades).map(({ roundTrip }) => roundTrip);
}

/** 매수 원본 연결은 내부에서만 보관해 기존 크로스탭 출력 계약을 유지한다. */
function entryRoundTripsWithEntry(trades: SimTradeLite[]) {
  interface Lot {
    entry: SimTradeLite;
    entry_rsi: number | null; entry_rsi_pctb: number | null; entry_rsi_pct_rank: number | null;
    entry_ma_state: string | null; entry_vol_ratio: number | null;
    entry_fgi: number | null; entry_regime: string | null; entry_weight_pct: number | null;
    entry_index_trend: SimTradeLite['entry_index_trend'];
    qty: number; origQty: number; pnl: number;
  }
  const lots = new Map<string, Lot[]>(); // 종목별 매수 lot 큐
  const done: { entry: SimTradeLite; roundTrip: EntryRoundTrip }[] = [];
  for (const t of filledSorted(trades)) {
    const q = lots.get(t.symbol) || [];
    if (t.side === 'buy') {
      const qty = Number(t.qty) || 0;
      q.push({
        entry: t,
        entry_rsi: t.entry_rsi ?? null,
        entry_rsi_pctb: t.entry_rsi_pctb ?? null,
        entry_rsi_pct_rank: t.entry_rsi_pct_rank ?? null,
        entry_ma_state: t.entry_ma_state ?? null,
        entry_vol_ratio: t.entry_vol_ratio ?? null,
        entry_fgi: t.entry_fgi ?? null,
        entry_regime: t.entry_regime ?? null,
        entry_weight_pct: t.entry_weight_pct ?? null,
        entry_index_trend: t.entry_index_trend ?? null,
        qty, origQty: qty, pnl: 0,
      });
    } else {
      let remain = Number(t.qty) || 0;
      const sellQty = remain; // 안분 분모 = 이 매도 수량
      const realized = Number(t.realized_pnl_bull) || 0;
      while (remain > 0 && q.length > 0) {
        const lot = q[0];
        const take = Math.min(remain, lot.qty);
        if (sellQty > 0) lot.pnl += realized * (take / sellQty); // 소비 수량 비례 귀속
        lot.qty -= take;
        remain -= take;
        if (lot.qty <= 1e-9) {
          done.push({ entry: lot.entry, roundTrip: {
            symbol: t.symbol,
            entry_rsi: lot.entry_rsi, entry_rsi_pctb: lot.entry_rsi_pctb,
            entry_rsi_pct_rank: lot.entry_rsi_pct_rank, entry_ma_state: lot.entry_ma_state,
            entry_vol_ratio: lot.entry_vol_ratio, entry_fgi: lot.entry_fgi,
            entry_regime: lot.entry_regime, entry_weight_pct: lot.entry_weight_pct,
            entry_index_trend: lot.entry_index_trend,
            pnl: lot.pnl, qty: lot.origQty,
          } });
          q.shift();
        }
      }
    }
    lots.set(t.symbol, q);
  }
  return done;
}

/**
 * 진입 라운드트립을 keyFn으로 그룹핑 → 구간별 {key,count,winRate,avgPnl,totalPnl}.
 * key null/빈값(미집계 진입)은 제외. pnl>0 = 승. count 내림차순 정렬.
 */
export function entryCrosstab(
  roundTrips: EntryRoundTrip[],
  keyFn: (rt: EntryRoundTrip) => string | null,
): EntryCrosstabRow[] {
  const g = new Map<string, { count: number; win: number; pnl: number }>();
  for (const rt of roundTrips) {
    const raw = keyFn(rt);
    if (raw == null || raw === '') continue;
    const key = String(raw);
    const e = g.get(key) || { count: 0, win: 0, pnl: 0 };
    const pnl = Number(rt.pnl) || 0;
    e.count++;
    e.pnl += pnl;
    if (pnl > 0) e.win++;
    g.set(key, e);
  }
  return [...g.entries()]
    .map(([key, e]) => ({
      key,
      count: e.count,
      winRate: Math.round((e.count ? e.win / e.count : 0) * 100),
      avgPnl: Math.round(e.count ? e.pnl / e.count : 0),
      totalPnl: Math.round(e.pnl),
    }))
    .sort((a, b) => b.count - a.count);
}

const KR_BENCHMARK = '^KS11';

/**
 * 지수추세위치 크로스탭 키 — KR(코스피)만 집계한다.
 * US 지수추세는 현행 게이트 보류 상태이므로 복기 통계에 포함하지 않는다.
 */
export function entryIndexTrendKey(t: Pick<EntryRoundTrip, 'entry_index_trend'>): string | null {
  const indexTrend = t.entry_index_trend;
  if (!indexTrend || indexTrend.benchmark !== KR_BENCHMARK) return null;
  return indexTrend.trend || null;
}

/**
 * 진입 크로스탭 묶음 — RSI·국면·비중 구간별 승률·평균손익 (무료 룰 복기 stats).
 */
export function entryStats(trades: SimTradeLite[]): EntryStats {
  const rt = entryRoundTrips(trades);
  return {
    roundTripCount: rt.length,
    byEntryRsi: entryCrosstab(rt, (t) => bucketRsi(t.entry_rsi)),       // 고정 30/70 (대조군)
    byEntryTiming: entryCrosstab(rt, (t) => diagnoseEntryTiming(t)),   // 결합 진단 (실험군 — fmkorea 1·2편)
    byEntryRegime: entryCrosstab(rt, (t) => t.entry_regime ?? null),
    byTimingRegime: entryCrosstab(rt, (t) => timingRegimeKey(t)),      // 타이밍×시장레짐 (fmkorea 3편)
    byEntryWeight: entryCrosstab(rt, (t) => bucketWeight(t.entry_weight_pct)),
    byEntryIndexTrend: entryCrosstab(rt, entryIndexTrendKey),         // G1 지수추세위치 (KR만·60일 관점)
  };
}

// ═══════════════════════════════════════════════════════════
// 유저 상태별 복기 모드 — 데이터 양에 따라 복기 종류 자동 선택 (결정4, 2026-06-17)
// 현행 sells<3 단일 차단의 맹점(매수만/장투/미매매 유저 진입 불가)을 4모드로 분기.
// 복기 모드와 진입 전용 통계의 단일 정본도 이 파일이다.
// ═══════════════════════════════════════════════════════════

/** 복기 모드 — 미매매·진입습관·장투·활발 */
export type ReviewMode = 'onboarding' | 'entry' | 'holding' | 'active';

/** 모드 판정 임계 (⚠ MVP 가정 — 운영 데이터로 보정) */
export const REVIEW_MODE_THRESHOLDS = {
  activeSells: 3,        // 청산 ≥ 3 → 활발(성과 복기 — 현행)
  holdingAgeDays: 14,    // 미청산 보유 평균 경과 ≥ 14일 → 장투 후보
  holdingMaxSymbols: 3,  // 순보유 종목 ≤ 3 → 장투(소수 종목 장기 보유)
};

/** 미청산 매수 lot (FIFO로 매도 소진 후 잔여 보유분) */
export interface OpenLot {
  symbol: string; ts: number; qty: number;
  entry_rsi: number | null; entry_regime: string | null; entry_weight_pct: number | null;
}

/** 미청산 매수 lot — entryRoundTrips와 동일 FIFO 골격, 완결분 대신 잔여 lot 반환. */
export function openLots(trades: SimTradeLite[]): OpenLot[] {
  const lots = new Map<string, OpenLot[]>();
  for (const t of filledSorted(trades)) {
    const q = lots.get(t.symbol) || [];
    if (t.side === 'buy') {
      q.push({
        symbol: t.symbol, ts: tsOf(t), qty: Number(t.qty) || 0,
        entry_rsi: t.entry_rsi ?? null, entry_regime: t.entry_regime ?? null, entry_weight_pct: t.entry_weight_pct ?? null,
      });
    } else {
      let remain = Number(t.qty) || 0;
      while (remain > 0 && q.length > 0) {
        const lot = q[0];
        const take = Math.min(remain, lot.qty);
        lot.qty -= take;
        remain -= take;
        if (lot.qty <= 1e-9) q.shift();
      }
    }
    lots.set(t.symbol, q);
  }
  const open: OpenLot[] = [];
  for (const q of lots.values()) for (const lot of q) if (lot.qty > 1e-9) open.push(lot);
  return open;
}

const targetHoldDays = (target: unknown, boughtAt: number): number | null => {
  if (typeof target === 'string') {
    return ({ '1m': 30, '3m': 90, '6m': 180, '12m': 365, '2y': 730 } as Record<string, number>)[target] ?? null;
  }
  if (!target || typeof target !== 'object' || Array.isArray(target)) return null;
  const date = (target as Record<string, unknown>).date;
  if (typeof date !== 'string') return null;
  const targetAt = new Date(date).getTime();
  const days = (targetAt - boughtAt) / DAY_MS;
  return Number.isFinite(days) && days > 0 ? days : null;
};

/**
 * 자랑 카탈로그 v2 표시용 후보 판정.
 * 서버 RPC·route가 최종 권위이며, 이 함수는 작성 UI에서 선택 가능한 과거/현재 사실만 미리 보여준다.
 */
export function classifyBragEligibility(
  trades: SimTradeLite[],
  positions: SimPositionLite[],
  now: number,
): BragEligibility[] {
  const filled = filledSorted(trades).filter((trade) => tsOf(trade) <= now);
  const rules = BRAG_ELIGIBILITY_RULES;
  const freshCutoff = now - rules.freshDays * DAY_MS;
  const positionBySymbol = new Map(positions.map((position) => [position.symbol, position]));
  const bySymbol = new Map<string, SimTradeLite[]>();
  for (const trade of filled) {
    const rows = bySymbol.get(trade.symbol) || [];
    rows.push(trade);
    bySymbol.set(trade.symbol, rows);
  }

  const out: BragEligibility[] = [];

  // 익절 완료 — 최근 14일 내 실현익 매도 1건마다 후보 1개.
  for (const trade of filled) {
    if (trade.side === 'sell'
        && (trade.realized_pnl_bull ?? 0) > 0
        && tsOf(trade) > freshCutoff
        && trade.id) {
      out.push({
        type: 'profit_take',
        symbol: trade.symbol,
        refTradeId: trade.id,
        label: `익절 완료 · ${trade.symbol}`,
      });
    }
  }

  for (const [symbol, rows] of bySymbol) {
    const buys = rows.filter((trade) => trade.side === 'buy');
    const sells = rows.filter((trade) => trade.side === 'sell');
    const position = positionBySymbol.get(symbol);

    // 사다리 익절 — 매도 2회+, 전량청산, 누적 실현익 양수, 최근 매도 14일 이내.
    const realizedSum = sells.reduce((sum, trade) => sum + Number(trade.realized_pnl_bull || 0), 0);
    const latestSellAt = sells.reduce((latest, trade) => Math.max(latest, tsOf(trade)), 0);
    if (sells.length >= 2 && !position && realizedSum > 0 && latestSellAt > freshCutoff) {
      out.push({ type: 'ladder', symbol, label: `사다리 익절 · ${symbol}` });
    }

    if (!position || (position.pnl_bull ?? 0) <= 0 || buys.length === 0) continue;

    // 평단 설계 — 같은 종목 체결 매수 3회+ + 현재 보유 평가익.
    if (buys.length >= rules.averagingMinBuys) {
      out.push({ type: 'averaging', symbol, label: `평단 설계 · ${symbol}` });
    }

    const firstBuyAt = Math.min(...buys.map(tsOf));
    const heldDays = (now - firstBuyAt) / DAY_MS;
    if (heldDays >= rules.holdMinDays) {
      out.push({ type: 'hold_long', symbol, label: `진득 보유 · ${symbol}` });
    }

    // 선언 보유 — 최근 매수의 구조화 선언, 기간 50% 충족, 현재 비중 1.5배 이내.
    const latestBuy = [...buys].sort((a, b) => tsOf(b) - tsOf(a))[0];
    const thesis = latestBuy.entry_thesis;
    const thesisObj = thesis && typeof thesis === 'object' && !Array.isArray(thesis)
      ? thesis as Record<string, unknown>
      : null;
    const declaredDays = targetHoldDays(thesisObj?.target_hold, tsOf(latestBuy));
    const targetWeight = Number(thesisObj?.target_weight_pct);
    const totalFromPortfolio = positions.find((row) => Number(row.portfolio_total_bull) > 0)?.portfolio_total_bull;
    const totalValue = Number(totalFromPortfolio)
      || positions.reduce((sum, row) => sum + Number(row.value_bull || 0), 0);
    const currentWeight = totalValue > 0 ? (Number(position.value_bull || 0) / totalValue) * 100 : Infinity;
    if (declaredDays != null
        && heldDays >= declaredDays * 0.5
        && Number.isFinite(targetWeight)
        && targetWeight > 0
        && currentWeight <= targetWeight * 1.5) {
      out.push({ type: 'declared_hold', symbol, label: `선언 보유 지킴 · ${symbol}` });
    }
  }

  return out;
}

/** 미청산 보유 lot의 수량가중 평균 경과일 + 순보유 종목 수 */
function holdingAge(open: OpenLot[], now: number): { symbols: number; avgAgeDays: number } {
  const symbols = new Set(open.map((l) => l.symbol));
  let wq = 0, qsum = 0;
  for (const l of open) { wq += ((now - l.ts) / DAY_MS) * l.qty; qsum += l.qty; }
  return { symbols: symbols.size, avgAgeDays: qsum > 0 ? wq / qsum : 0 };
}

/**
 * 복기 모드 판정 — 미매매(매수0)→활발(청산3+)→장투(소수 종목 장기 보유)→진입습관(나머지 매수만).
 * ⚠ 과거 now 주입 경로(복기 재생·백테스트·튜너)는 tradesAsOf(trades, now) 선필터 의무 (look-ahead cut).
 */
export function reviewMode(trades: SimTradeLite[], now: number): ReviewMode {
  const th = REVIEW_MODE_THRESHOLDS;
  const all = trades || [];
  const buys = all.filter((t) => t.side === 'buy' && t.price != null);
  const sells = all.filter((t) => t.side === 'sell' && t.realized_pnl_bull != null);
  if (buys.length === 0) return 'onboarding';
  if (sells.length >= th.activeSells) return 'active';
  const { symbols, avgAgeDays } = holdingAge(openLots(all), now);
  if (symbols > 0 && symbols <= th.holdingMaxSymbols && avgAgeDays >= th.holdingAgeDays) return 'holding';
  return 'entry';
}

/** 매수행 entry_* 카운트 분포 (청산 없이 — 진입 빈도만) */
function entryCountTab(buys: SimTradeLite[], keyFn: (t: SimTradeLite) => string | null): { key: string; count: number }[] {
  const g = new Map<string, number>();
  for (const t of buys) {
    const raw = keyFn(t);
    if (raw == null || raw === '') continue;
    g.set(String(raw), (g.get(String(raw)) || 0) + 1);
  }
  return [...g.entries()].map(([key, count]) => ({ key, count })).sort((a, b) => b.count - a.count);
}

/** 매수 기반 진입 분포 (매수만·장투 유저용) — 청산 없이도 entry_* 빈도 + 물타기 + 보유 경과·집중도 */
export interface EntryOnlyStats {
  buyCount: number; holdingSymbols: number; avgHoldingAgeDays: number;
  averagingDown: number; topWeightPct: number | null;
  /** 불타기(평단 위 추가매수) 횟수 — 사실 수치, 판정 없음 (2026-10-03) */
  pyramiding: number;
  /** 한 번 보유하는 동안 한 종목 최대 물타기 횟수 — 요약 칸 경고 기준(averagingDownPerPositionWarn) 비교용 */
  maxAveragingDownPerPosition: number;
  byEntryRsi: { key: string; count: number }[];
  byEntryRegime: { key: string; count: number }[];
  byEntryWeight: { key: string; count: number }[];
}

/** ⚠ 과거 now 주입 경로(복기 재생·백테스트·튜너)는 tradesAsOf(trades, now) 선필터 의무 (look-ahead cut). */
export function entryOnlyStats(trades: SimTradeLite[], now: number): EntryOnlyStats {
  const all = trades || [];
  const buys = all.filter((t) => t.side === 'buy' && t.price != null);
  const { symbols, avgAgeDays } = holdingAge(openLots(all), now);
  const weights = buys.map((t) => Number(t.entry_weight_pct)).filter((v) => Number.isFinite(v));
  const addOn = addOnBreakdown(all);
  return {
    buyCount: buys.length,
    holdingSymbols: symbols,
    avgHoldingAgeDays: Math.round(avgAgeDays * 10) / 10,
    averagingDown: addOn.averagingDown,
    pyramiding: addOn.pyramiding,
    maxAveragingDownPerPosition: addOn.maxAveragingDownPerPosition,
    topWeightPct: weights.length ? Math.round(Math.max(...weights)) : null,
    byEntryRsi: entryCountTab(buys, (t) => bucketRsi(t.entry_rsi)),
    byEntryRegime: entryCountTab(buys, (t) => t.entry_regime ?? null),
    byEntryWeight: entryCountTab(buys, (t) => bucketWeight(t.entry_weight_pct)),
  };
}
